import React from 'react';
import { Card, Table, Row, Col, Statistic, Space, Tag, Button, Tooltip } from 'antd';
import { PauseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import type { Region, Service } from '../mockData';

interface RegionConfigProps {
  service?: Service;
  regions: Region[];
}

const RegionConfig: React.FC<RegionConfigProps> = ({ service, regions }) => {
  const expandedRowRender = (region: Region) => {
    const columns = [
      {
        title: '地址',
        dataIndex: 'target',
        key: 'target',
      },
      {
        title: '权重',
        dataIndex: 'weight',
        key: 'weight',
      },
      {
        title: '健康状态',
        dataIndex: 'health_status',
        key: 'health_status',
        render: (status: string) => (
          <Tag color={status === 'healthy' ? 'success' : 'error'}>
            {status === 'healthy' ? '健康' : '不健康'}
          </Tag>
        ),
      },
      {
        title: '节点状态',
        dataIndex: 'node_status',
        key: 'node_status',
        render: (status: string) => (
          <Tag color={status === 'active' ? 'success' : 'error'}>
            {status === 'active' ? '已启用' : '已禁用'}
          </Tag>
        ),
      },
      {
        title: '创建时间',
        dataIndex: 'created_at',
        key: 'created_at',
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        key: 'updated_at',
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={region.targets}
        pagination={false}
        rowKey="id"
      />
    );
  };

  const columns = [
    {
      title: '地址',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Region) => (
        <Space>
          {name}
          {record.tags?.map((tag) => (
            <Tag key={tag} color="orange">{tag}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '健康状态',
      dataIndex: 'health_status',
      key: 'health_status',
      render: (_: string, record: Region) => {
        const totalTargets = record.targets.length;
        const healthyTargets = record.targets.filter(t => t.health_status === 'healthy').length;
        const isAllHealthy = healthyTargets === totalTargets;
        
        return (
          <Space>
            <Tag color={isAllHealthy ? 'success' : 'error'}>
              {isAllHealthy ? '健康' : '不健康'}
            </Tag>
            <span style={{ 
              color: isAllHealthy ? '#52c41a' : '#ff4d4f',
              fontSize: '12px'
            }}>
              ({healthyTargets}/{totalTargets})
            </span>
          </Space>
        );
      },
    },
    {
      title: '节点状态',
      dataIndex: 'node_status',
      key: 'node_status',
      render: (_: string, record: Region) => {
        const totalTargets = record.targets.length;
        const activeTargets = record.targets.filter(t => t.node_status === 'active').length;
        const isAllActive = activeTargets === totalTargets;
        
        return (
          <Space>
            <Tag color={isAllActive ? 'success' : 'error'}>
              {isAllActive ? '已启用' : '部分禁用'}
            </Tag>
            <span style={{ 
              color: isAllActive ? '#52c41a' : '#ff4d4f',
              fontSize: '12px'
            }}>
              ({activeTargets}/{totalTargets})
            </span>
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
    },
    {
      title: '操作',
      key: 'action',
      render: () => (
        <Space>
          <Tooltip title="禁用">
            <Button type="text" icon={<PauseCircleOutlined />} />
          </Tooltip>
          <Tooltip title="删除">
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* 上游服务配置卡片 */}
        <Col span={12}>
          <Card title="上游服务配置" bordered={false}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="服务地址"
                  value={service?.host || 'user-api.internal'}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="服务端口"
                  value={service?.port || 8080}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="协议类型"
                  value={service?.protocol?.toUpperCase() || 'HTTP'}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="负载均衡算法"
                  value="Round Robin"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        
        {/* 健康检查配置卡片 */}
        <Col span={12}>
          <Card title="健康检查配置" bordered={false}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="检查间隔"
                  value="5s"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="超时时间"
                  value="3s"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="健康阈值"
                  value="2次"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="不健康阈值"
                  value="3次"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Table
        className="region-table"
        columns={columns}
        expandable={{
          expandedRowRender,
          defaultExpandedRowKeys: ['region-001'],
        }}
        dataSource={regions}
        rowKey="id"
        pagination={false}
      />
    </>
  );
};

export default RegionConfig; 